import { z } from 'zod';
import { IdSchema, E164Schema, MoneySchema, TimestampSchema, ListQuerySchema, BoolSchema } from './common.js';

export const CallDirectionSchema = z.enum(['inbound', 'outbound', 'internal']);

export const CdrSchema = z.object({
  id: IdSchema,
  call_uuid: IdSchema,
  tenant_id: IdSchema,
  direction: CallDirectionSchema,
  leg: z.enum(['a', 'b']),
  from_number: z.string().nullable(),
  from_name: z.string().nullable(),
  to_number: z.string().nullable(),
  destination_e164: E164Schema.nullable(),
  extension_id: IdSchema.nullable(),
  started_at: TimestampSchema,
  answered_at: TimestampSchema.nullable(),
  ended_at: TimestampSchema,
  duration_sec: z.number().int(),
  billable_sec: z.number().int(),
  hangup_cause: z.string().nullable(),
  sip_code: z.number().int().nullable(),
  // Quality
  mos: z.string().nullable(),
  jitter_ms: z.string().nullable(),
  packet_loss_pct: z.string().nullable(),
  codec: z.string().nullable(),
  // Money -- strings, never numbers. See MoneySchema.
  currency: z.string().length(3).nullable(),
  sell_amount: MoneySchema.nullable(),
  /** Platform and reseller scopes only; stripped for tenant scope in the handler. */
  cost_amount: MoneySchema.nullable().optional(),
  margin_amount: MoneySchema.nullable().optional(),
  has_recording: z.boolean(),
  /** What the agent said the call was (0108). Absent when nobody has said. */
  disposition: z.object({
    code: z.string(),
    label: z.string(),
    kind: z.enum(['positive', 'neutral', 'negative']),
    note: z.string().nullable(),
  }).nullable().optional(),
  /**
   * Whether a recorded call can be played: ready once its node reports the upload,
   * saving until then, unavailable if it never reached storage. Null when not recorded.
   */
  recording_status: z.enum(['ready', 'saving', 'unavailable']).nullable().optional(),
  /** The recording itself, when there is one: what it should hash to, what it is, how big. */
  recording: z.object({
    sha256: z.string().nullable(),
    source_sha256: z.string().nullable(),
    format: z.string(),
    bytes: z.number().nullable(),
  }).optional(),
});
export type Cdr = z.infer<typeof CdrSchema>;

/**
 * Filterable fields are declared explicitly. Anything else is a 422 rather than
 * a silently ignored parameter, which is what stops an unindexed full scan
 * reaching the database (doc 05 section 3).
 */
export const CdrListQuerySchema = ListQuerySchema.extend({
  /**
   * A call the switch carries has two records: the caller's leg and the one towards the carrier.
   * Listing both reads as every call twice (owner, 20 September 2026), so only the caller's leg is
   * listed unless 'all' is asked for. Both are kept: cost and quality are per leg.
   */
  legs: z.enum(['main', 'all']).default('main'),
  'started_at[gte]': TimestampSchema.optional(),
  'started_at[lt]': TimestampSchema.optional(),
  direction: CallDirectionSchema.optional(),
  extension_id: IdSchema.optional(),
  'destination_e164[prefix]': z.string().max(20).optional(),
  // Not z.coerce.boolean(): that parses 'false' as true, so filtering for
  // UNANSWERED calls returned answered ones. See BoolSchema.
  answered: BoolSchema.optional(),
  /** Only calls that were recorded. */
  recorded: BoolSchema.optional(),
  q: z.string().max(64).optional(),
  sort: z.enum(['started_at', '-started_at', 'duration_sec', '-duration_sec']).default('-started_at'),
});
export type CdrListQuery = z.infer<typeof CdrListQuerySchema>;

export const CdrStatsSchema = z.object({
  window: z.object({ from: TimestampSchema, to: TimestampSchema }),
  total_calls: z.number().int(),
  answered_calls: z.number().int(),
  answer_rate_pct: z.number(),
  total_duration_sec: z.number().int(),
  avg_duration_sec: z.number(),
  inbound: z.number().int(),
  outbound: z.number().int(),
  internal: z.number().int(),
  spend: MoneySchema.nullable(),
  currency: z.string().length(3).nullable(),
});
export type CdrStats = z.infer<typeof CdrStatsSchema>;
